import React, { Component } from "react";
import { Row, Col, Collapse, Button } from "react-bootstrap";
import { styled } from "@mui/material/styles";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment";

//Components
import SearchFilter from "../CustomUIComponents/SearchFilter/SearchFilter";
import Pagination from "../CustomUIComponents/Pagination/Pagination";
import CustomNotesModal from "../CustomUIComponents/CustomNotesModal/CustomNotesModal";

//Images
import CSVdownload from "../../resources/images/ic_download_csv.svg";
import PDFdownload from "../../resources/images/ic_download_pdf.svg";

// Strings
import { Strings } from "../../resources/i18n/i18n";

//Constants
import {
  ENTRY_LEVEL_STATUS,
  ENTRY_LEVEL_REQUEST_STATUS,
  ENTRY_LEVEL_STATUS_FILTER_OPTIONS,
  PROSPECT_REGISTRATION_STATUS,
  ENTRY_LEVEL_STATUS_ALL,
  SELLER_FILTER_ALL,
  ENTRY_LEVEL_REPORT_DOWNLOAD,
  FIRST_ROW_COLUMNS,
  SECOND_ROW_COLUMNS,
  LOAN_DETAILS_COLUMNS,
  DECISION_DETAILS_COLUMNS,
} from "./Constants";
import {
  CURRENCY,
  SEARCH_FILTER_TYPE,
  SEARCH_FILTER_FIELDS,
  DATE_TYPE,
  DEFAULT_DATE_FORMAT,
  DEFAULT_DURATION,
  DOWNLOAD_FILE_TYPE,
} from "../../constants/appConstants";
import {
  DEFAULT_PAGINATION_RECORDS_COUNT,
  DEFAULT_PAGINATION_ACTIVE_PAGE,
} from "../CustomUIComponents/Pagination/PaginationConstants";

//Utility
import Utility from "../../utils/Utility";

//styles
import "./EntryLevelApproval.scss";

// MC-5742: the tab is a worklist first, so it opens on the requests still waiting
// for a decision; the status filter widens it to the approved ones.
const DEFAULT_FILTERS = {
  fromDate: DEFAULT_DURATION.fromDate,
  toDate: DEFAULT_DURATION.toDate,
  sellerId: SELLER_FILTER_ALL,
  status: ENTRY_LEVEL_REQUEST_STATUS.PENDING_APPROVAL,
};

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

class EntryLevelApproval extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filters: { ...DEFAULT_FILTERS },
      activePage: DEFAULT_PAGINATION_ACTIVE_PAGE,
      recordsPerPage: DEFAULT_PAGINATION_RECORDS_COUNT,
      // MC-5742: the decision the approver is about to take, held while the
      // note modal collects the reason for it
      pendingDecision: null,
    };
  }

  componentDidMount() {
    this.fetchRequests();
  }

  handleDateChange = (key) => (dateMoment) => {
    let dateValue = "";
    if (key === DATE_TYPE.FROM_DATE) {
      if (dateMoment.isAfter(this.state.filters.toDate)) {
        dateValue = this.state.filters.fromDate;
      } else {
        dateMoment.set({ hour: 0, minute: 0, second: 0 });
        dateValue = dateMoment.format(DEFAULT_DATE_FORMAT);
      }
    } else if (dateMoment.isBefore(this.state.filters.fromDate)) {
      dateValue = this.state.filters.toDate;
    } else {
      dateValue = dateMoment.format(DEFAULT_DATE_FORMAT);
    }
    this.setState({
      filters: { ...this.state.filters, [key]: dateValue },
    });
  };

  handleSellerChange = (event) => {
    this.setState({
      filters: { ...this.state.filters, sellerId: event.target.value === SELLER_FILTER_ALL ? '': event.target.value },
    });
  };

  handleStatusChange = (event) => {
    this.setState({
      filters: { ...this.state.filters, status: event.target.value },
    });
  };

  // "All" is a UI-only value: st is bound to an enum on the backend, so the
  // param has to be left out entirely rather than sent as the sentinel.
  selectedStatus = () => {
    const { status } = this.state.filters;
    return status && status !== ENTRY_LEVEL_STATUS_ALL ? status : undefined;
  };

  // The seller dropdown opens on a placeholder that means "every seller", so it
  // is stripped the same way rather than sent as a user id.
  selectedSeller = () => {
    const { sellerId } = this.state.filters;
    return sellerId && sellerId !== SELLER_FILTER_ALL ? sellerId : undefined;
  };

  buildSearchFilterObject = () => {
    const { fromDate, toDate } = this.state.filters;
    return {
      st: this.selectedStatus(),
      fd: fromDate ? Utility.convertDateIntoMiliSeconds(fromDate) : undefined,
      td: toDate ? Utility.convertDateIntoMiliSeconds(toDate) : undefined,
      uid: this.selectedSeller(),
      lmt: this.state.recordsPerPage,
      // the API pages from 0, the paginator counts from 1
      oft: this.state.activePage - 1,
    };
  };

  // MC-5742: the report covers everything the current filters match, not just the
  // page on screen, so it deliberately leaves lmt/oft out.
  downloadFile = (fileType) => {
    const { fromDate, toDate } = this.state.filters;
    const status = this.selectedStatus();
    const sellerId = this.selectedSeller();
    let downloadUrl =
      ENTRY_LEVEL_REPORT_DOWNLOAD.downloadUrl + "?fileType=" + fileType;
    if (status) {
      downloadUrl += "&st=" + status;
    }
    if (fromDate) {
      downloadUrl += "&fd=" + Utility.convertDateIntoMiliSeconds(fromDate);
    }
    if (toDate) {
      downloadUrl += "&td=" + Utility.convertDateIntoMiliSeconds(toDate);
    }
    if (sellerId) {
      downloadUrl += "&uid=" + sellerId;
    }
    // When the selected range exceeds 7 days the backend emails the report
    // instead of streaming a file (ReportDownloadController threshold).
    const rangeDays = moment(toDate).diff(moment(fromDate), "days");
    const isDownloadFile = !fromDate || !toDate || rangeDays <= 7;
    this.props.downloadReports(downloadUrl, fileType, isDownloadFile);
  };

  fetchRequests = () => {
    this.props.handleOnClickSearchFilter(this.buildSearchFilterObject());
  };

  // A new filter can shrink the result set, so always come back to the first
  // page instead of asking for a page that may no longer exist.
  handleOnClickSearchFilter = () => {
    this.setState(
      { activePage: DEFAULT_PAGINATION_ACTIVE_PAGE },
      this.fetchRequests
    );
  };

  handleOnClickResetFilter = () => {
    this.setState(
      {
        filters: { ...DEFAULT_FILTERS },
        activePage: DEFAULT_PAGINATION_ACTIVE_PAGE,
        recordsPerPage: DEFAULT_PAGINATION_RECORDS_COUNT,
      },
      this.fetchRequests
    );
  };

  onPageChange = ({ page, pageLength }) => {
    this.setState(
      {
        activePage:
          pageLength !== this.state.recordsPerPage
            ? DEFAULT_PAGINATION_ACTIVE_PAGE
            : page,
        recordsPerPage: pageLength,
      },
      this.fetchRequests
    );
  };

  // MC-5742: neither decision is submitted straight from the row - the approver
  // has to give a reason first, which reaches the salesperson as a note on the loan.
  openDecisionModal = (minimartId, status) => {
    this.setState({ pendingDecision: { minimartId, status } });
  };

  closeDecisionModal = () => {
    this.setState({ pendingDecision: null });
  };

  submitDecision = (note) => {
    const { minimartId, status } = this.state.pendingDecision;
    // The modal only closes once the decision is accepted, so a rejected note or a
    // failed decision keeps what was typed on screen with the error above it.
    this.props.handleDecision(minimartId, status, note, this.closeDecisionModal);
  };

  renderDecisionModal = () => {
    const { pendingDecision } = this.state;
    if (!pendingDecision) {
      return null;
    }
    const isRejection = pendingDecision.status === ENTRY_LEVEL_STATUS.REJECTED;
    return (
      <CustomNotesModal
        open={true}
        modalHeader={Strings(
          isRejection ? "rejectEntryLevelRequest" : "approveEntryLevelRequest"
        )}

        modalText1Class={"informationText"}
        closeModal={this.closeDecisionModal}
        modalWidthClass={"customModal"}
        modalButtonOk={{
          text: Strings("Submit").toUpperCase(),
          onClick: this.submitDecision,
        }}
        modalButtonCancel={{
          text: Strings("Cancel"),
          onClick: this.closeDecisionModal,
        }}
        errorMessage={this.props.errorMessage}
      />
    );
  };

  renderSearchFilter = () => {
    const searchFilters = [
      {
        type: SEARCH_FILTER_TYPE.DATE,
        fromDate: this.state.filters.fromDate,
        toDate: this.state.filters.toDate,
        handleCalenderChange: this.handleDateChange,
        fieldname: SEARCH_FILTER_FIELDS.DATE,
        column: 4,
      },
      {
        type: SEARCH_FILTER_TYPE.SELECTION,
        handleOnChange: this.handleSellerChange,
        Value: this.state.filters.sellerId,
        fieldname: SEARCH_FILTER_FIELDS.SALES_PERSON_NAME,
        filterDropdownList: this.props.sellerOptions,
        name: "sellerId",
        column: 2,
      },
      {
        type: SEARCH_FILTER_TYPE.SELECTION,
        handleOnChange: this.handleStatusChange,
        Value: this.state.filters.status,
        fieldname: SEARCH_FILTER_FIELDS.STATUS,
        filterDropdownList: ENTRY_LEVEL_STATUS_FILTER_OPTIONS,
        name: "status",
        column: 2,
      },
    ];
    return (
      <SearchFilter
        filters={searchFilters}
        handleOnClickSearchFilter={this.handleOnClickSearchFilter}
        handleOnClickResetFilter={this.handleOnClickResetFilter}
        paddingLeft={2}
      />
    );
  };

  renderDownloadButtons = () => {
    return (
      <div className='text-end pe-3 flex-shrink-0'>
        <Button
          className='downloadButtons me-3'
          onClick={() => this.downloadFile(DOWNLOAD_FILE_TYPE.PDF)}
        >
          <img src={PDFdownload} alt='' />
        </Button>
        <Button
          className='downloadButtons'
          onClick={() => this.downloadFile(DOWNLOAD_FILE_TYPE.CSV)}
        >
          <img src={CSVdownload} alt='' />
        </Button>
      </div>
    );
  };

  renderValue = (request, column) => {
    const value = request[column.key];
    if (!value && value !== 0) {
      return Strings("NA");
    }
    if (column.format === "date") {
      return Utility.getFormattedDateString(value);
    }
    return Strings(value);
  };

  renderHeaderRow = () => {
    return (
      <Card className='mt-3 headerRow py-2'>
        <CardActions disableSpacing>
          {FIRST_ROW_COLUMNS.map((column) => (
            <Col
              md={column.key === "action" ? 1 : undefined}
              key={column.key}
              className={
                column.key === "action" ? "text-end pe-3" : "text-center"
              }
            >
              <Typography className='headerContent'>
                {Strings(column.name)}
              </Typography>
            </Col>
          ))}
        </CardActions>
      </Card>
    );
  };

  renderFirstRow = (request) => {
    return (
      <CardActions>
        {FIRST_ROW_COLUMNS.map((column) =>
          column.key === "action" ? (
            <Col md={1} key={column.key} className='text-end pe-3'>
              <ExpandMore
                expand={this.props.expanded.includes(request.id)}
                onClick={() => this.props.handleExpandClick(request.id)}
                aria-label='show more'
                className='cursorPointer'
              >
                <ExpandMoreIcon />
              </ExpandMore>
            </Col>
          ) : (
            <Col key={column.key} className='text-center' title={Strings(column.name)}>
              <Typography className='content'>
                {this.renderValue(request, column)}
              </Typography>
            </Col>
          )
        )}
      </CardActions>
    );
  };

  renderDetail = (request, column) => {
    return (
      <Col md={3} key={column.key} className='py-1'>
        <span className='label'>{Strings(column.name)}{" : "}</span>
        <span className='content'>{this.renderValue(request, column)}</span>
      </Col>
    );
  };

  renderLoanValue = (loan, column) => {
    const value = loan[column.key];
    if (!value && value !== 0) {
      return Strings("NA");
    }
    if (column.format === "currency") {
      return CURRENCY + Utility.getCurrencyRepresentationOfAmount(value);
    }
    if (column.format === "days") {
      return Utility.convertCollectionDaysToSpanish(value.split(","));
    }
    return Strings(value);
  };

  renderLoanDetail = (loan, column) => {
    return (
      <Col md={3} key={column.key} className='py-1'>
        <span className='label'>{Strings(column.name)}{" : "}</span>
        <span className='content'>{this.renderLoanValue(loan, column)}</span>
      </Col>
    );
  };

  renderCollapseSection = (request) => {
    const loan = request.requestedLoanInfo || {};
    const isPending =
      request.entryLevelRequestStatus ===
      ENTRY_LEVEL_REQUEST_STATUS.PENDING_APPROVAL;
    const isDecided =
      request.entryLevelRequestStatus ===
        ENTRY_LEVEL_REQUEST_STATUS.APPROVED ||
      request.entryLevelRequestStatus ===
        ENTRY_LEVEL_REQUEST_STATUS.REJECTED;
    return (
      <Collapse
        in={this.props.expanded.includes(request.id)}
        timeout={200}
        unmountOnExit
      >
        <CardContent className='pt-0 arrowPointer tableRow'>
          <hr className='my-2 opacity-100' />
          {/* MC-5796: tells the approver this level was asked for on a prospect
              already in the book, not chosen while registering a new customer. */}
          {request.registrationStatus === PROSPECT_REGISTRATION_STATUS && (
            <Row>
              <Col md={12} className='py-1 prospectRequestNote'>
                {Strings("prospectLevelRequestNote")}
              </Col>
            </Row>
          )}
          <Col md={12} className='headerContent'>
            {Strings("minimartDetailsText")}
          </Col>
          <Row>
            {SECOND_ROW_COLUMNS.map((column) =>
              this.renderDetail(request, column)
            )}
          </Row>
          <Col md={12} className='headerContent mt-2 pt-2 border-top'>
            {Strings("loanDetails")}
          </Col>
          <Row>
            {LOAN_DETAILS_COLUMNS.map((column) =>
              this.renderLoanDetail(loan, column)
            )}
          </Row>
          {isDecided && (
            <>
              <Col md={12} className='headerContent mt-2 pt-2 border-top'>
                {Strings("decisionDetails")}
              </Col>
              <Row>
                {DECISION_DETAILS_COLUMNS.map((column) =>
                  this.renderDetail(request, column)
                )}
              </Row>
              {/* The reason is free text the approver typed, so it gets a row
                  of its own and is rendered as-is rather than through Strings(). */}
              <Row>
                <Col md={12} className='py-1'>
                  <span className='label'>
                    {Strings("Note")}
                    {" : "}
                  </span>
                  <span className='content'>
                    {request.decisionNote || Strings("NA")}
                  </span>
                </Col>
              </Row>
            </>
          )}
          <Row className='pt-2'>
            <Col className='text-end'>
              {/* A decided request keeps its buttons so the row reads the same,
                  but they are dead - there is nothing left to decide. */}
              <Button
                className='rejectButtonBackground w-auto mx-2'
                disabled={!isPending}
                onClick={() =>
                  this.openDecisionModal(request.id, ENTRY_LEVEL_STATUS.REJECTED)
                }
              >
                {Strings("reject")}
              </Button>
              <Button
                className='buttonBackground w-auto mx-2'
                disabled={!isPending}
                onClick={() =>
                  this.openDecisionModal(request.id, ENTRY_LEVEL_STATUS.APPROVED)
                }
              >
                {Strings("approve")}
              </Button>
            </Col>
          </Row>
        </CardContent>
      </Collapse>
    );
  };

  renderRows = () => {
    return this.props.requests.map((request) => (
      <Card
        key={request.id}
        className={
          this.props.expanded.includes(request.id)
            ? "highlighted tableRow py-1 allRows"
            : "tableRow py-1 allRows"
        }
      >
        {this.renderFirstRow(request)}
        {this.renderCollapseSection(request)}
      </Card>
    ));
  };

  renderRequests = () => {
    return (
      <div className='paper customTableCard'>
        {/* MC-5796: the download buttons share the filter row so their right edge
            lines up with it, instead of hanging off a row of their own. */}
        <div className='d-flex align-items-center'>
          <div className='flex-grow-1'>{this.renderSearchFilter()}</div>
          {this.renderDownloadButtons()}
        </div>
        {this.props.errorMessage && (
          <div className='errors'>{this.props.errorMessage}</div>
        )}
        {this.props.requests.length > 0 ? (
          <div className='mx-5'>
            {this.renderHeaderRow()}
            {this.renderRows()}
            <Row className='pt-3'>
              <Col md={12}>
                <Pagination
                  activePage={this.state.activePage}
                  recordsPerPage={this.state.recordsPerPage}
                  numberOfPages={this.props.numberOfPages}
                  onPageChange={this.onPageChange}
                  dropup={true}
                />
              </Col>
            </Row>
          </div>
        ) : (
          <div className='noRecordsFound'>{Strings("NoRecordsFound")}</div>
        )}
      </div>
    );
  };

  render() {
    return (
      <div className='entryLevelApproval'>
        {this.renderDecisionModal()}
        <div className='heading'>{Strings("EntryLevelApproval")}</div>
        {this.renderRequests()}
      </div>
    );
  }
}

export default EntryLevelApproval;
